import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import { z } from 'zod'
import { kUserSkillsPath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { UserSkillSchema, type UserSkill, type UserSkillWrite } from '@/user-skills/schemas'
import { getResponseDebugContext, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

export default async function postUserSkill(content: UserSkillWrite): Promise<UserSkill> {
  const sentryTransactionId = nanoid()
  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kUserSkillsPath}`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
      },
      body: JSON.stringify(content),
    })
    if (response.status === 409) {
      throw new ApiError(response.status, 'A skill with this name already exists. Choose a different name.')
    }
    if (response.status === 422) {
      const { data } = await readResponseJson(response)
      const validation = z.object({ detail: z.array(z.object({ msg: z.string() })) }).safeParse(data)
      const message = (validation.success ? validation.data.detail.map(({ msg }) => msg).join('; ') : '') || 'The skill could not be saved.'
      throw new ApiError(response.status, message)
    }
    if (!response.ok) {
      const error = new ApiError(response.status, 'The skills request failed. Please try again.')
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, { extra: getResponseDebugContext(response, apiUrl) })
      })
      throw error
    }

    const { data, parseError } = await readResponseJson(response)
    if (parseError) {
      const error = new ApiResponsePayloadError(response.status, 'Invalid skills response JSON')
      Sentry.captureException(error, { extra: getResponseDebugContext(response, apiUrl) })
      throw error
    }

    const parsed = UserSkillSchema.safeParse(data)
    if (!parsed.success) {
      const error = new ApiResponsePayloadError(response.status, 'Invalid skills response shape', parsed.error.issues)
      Sentry.captureException(error, { extra: { ...getResponseDebugContext(response, apiUrl), issues: parsed.error.issues } })
      throw error
    }
    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
